import React, { FC } from 'react'
import { RouteComponentProps, Route, Switch } from 'react-router-dom'

// Constants
import { RouteParams, routes } from '@sketch/constants'

// Hooks
import { useURLQuerySearch } from 'utils'
import { useForTablet } from '@sketch/components'
import { useGetWorkspaces } from 'modules/workspace/operations'
import { useEducationWorkspaceApproved } from 'modules/workspace/modals/EducationWorkspaceApprovedModal'

// Utils
import {
  isTrashRoute,
  isDraftsRoute,
  isLibrariesRoute,
  isProjectRoute,
} from 'utils/route'

import { getSectionTitle, getProjectTitle } from './utils'

// Components
import WorkspaceSidebarLayout from 'modules/workspace/components/WorkspaceSidebarLayout'
import NotFoundView from 'views/NotFoundView'
import WorkspaceSharesList from 'modules/shares/components/WorkspaceSharesList'
import TrashList from 'modules/shares/components/TrashList'
import ShareNavbar from 'modules/shares/components/ShareNavbar'
import ProjectsView from 'modules/projects/views/ProjectsView'
import Banner from 'containers/Banner'
import { PrivateWorkspaceRouteExtraProps } from 'modules/workspace/containers'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import {
  BillingStatusBanners,
  WelcomeModalTrigger,
  PersonalWorkspaceBanners,
} from 'modules/workspace/components'
import { getCanAdministerShares } from 'modules/shares/utils'
import { SearchBar } from 'modules/shares/components/SearchBar'
import { useApolloClient } from '@apollo/react-hooks'

// Types
import {
  useGetProjectsQuery,
  useGetWorkspaceSharesCountQuery,
  useGetDocumentListSettingsQuery,
  useGetWorkspaceBillingQuery,
} from '@sketch/gql-types'

// Styles
import {
  NavbarTitle,
  SearchBarWrapper,
  BannerWrapper,
} from './WorkspaceDocumentsView.styles'

/**
 * TYPES
 */
export type WorkspaceDocumentsViewRoutesProps = RouteComponentProps<
  | RouteParams<'WORKSPACE_PROJECT'>
  | RouteParams<'WORKSPACE_SHARES'>
  | RouteParams<'WORKSPACE_TRASH'>
>

export type WorkspaceDocumentsViewProps = PrivateWorkspaceRouteExtraProps &
  WorkspaceDocumentsViewRoutesProps

type ProjectsViewProps = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_PROJECT'> | RouteParams<'WORKSPACE_DRAFTS'>>
>

type WorkspaceTrashRouteParams = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_TRASH'>>
>
type WorkspaceSharesRouteParams = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_SHARES'>>
>

type WorkspaceLibrariesRouteParams = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_LIBRARIES'>>
>

type WorkspaceSharedWithMeRouteParams = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_SHARED_WITH_ME'>>
>

/**
 * COMPONENT
 */
const WorkspaceDocumentsView: FC<WorkspaceDocumentsViewProps> = ({
  location,
  history,
  match: { params, path },
  workspace,
}) => {
  const { workspaceId } = params
  const isTabletAndBigger = useForTablet()
  const isSmallerThanTablet = !isTabletAndBigger

  const isWorkspaceLibrariesRoute = isLibrariesRoute(path)

  const [search, setSearchValue, debouncedSearch] = useURLQuerySearch(
    location,
    history
  )

  const { data, loading } = useGetProjectsQuery({
    variables: {
      workspaceId,
    },
  })

  const { data: settings } = useGetDocumentListSettingsQuery()
  const { showDocumentsInProjects } = settings || {}

  const workspaceSharesCount = useGetWorkspaceSharesCountQuery({
    variables: { identifier: workspaceId },
    fetchPolicy: 'cache-and-network',
  })
  const sharesCount =
    workspaceSharesCount.data?.workspace.shares.meta.totalCount ?? 0

  const workspacesQuery = useGetWorkspaces()

  const billingQuery = useGetWorkspaceBillingQuery({
    variables: { identifier: workspaceId },
    skip: !workspace.customer?.identifier,
  })

  useEducationWorkspaceApproved(workspace)
  const apolloClient = useApolloClient()
  const cache = apolloClient.cache
  let projectFromCache = null

  const subscription = billingQuery.data?.workspace?.customer?.billing

  if (loading && isDraftsRoute(path)) {
    return <DynamicLoadingPage />
  }

  const isUserEditor = workspace.userCanEdit
  const canUserAdministerShares = getCanAdministerShares(workspace)
  const hasDraftsProject = !!data?.workspace.draftsProject.entries[0]

  const isUserAdmin = workspace.userRole === 'ADMIN'
  const isMember = isUserAdmin || workspace.userRole === 'MEMBER'
  const { status, subscriptionEnd } = subscription ?? {}

  const isEducationWorkspace = workspace.type === 'STUDENT'

  if (isProjectRoute(path)) {
    projectFromCache = getProjectTitle(params, cache)
  }

  if (isTrashRoute(path) && !canUserAdministerShares) {
    return <NotFoundView />
  }

  if (isDraftsRoute(path) && !hasDraftsProject) {
    // The user is accessing /workspace/${workspaceId}/drafts but there isn't any
    // draft project in this workspace.
    return <NotFoundView />
  }

  const sectionTitle = projectFromCache || getSectionTitle(path)

  return (
    <WorkspaceSidebarLayout
      title={sectionTitle || 'Documents'}
      workspaces={workspacesQuery.workspaces ?? [workspace]}
      currentWorkspace={workspace}
      navbar={
        <ShareNavbar
          search={search}
          onSearchChange={setSearchValue}
          userRole={workspace.userRole}
          title={<NavbarTitle>{sectionTitle}</NavbarTitle>}
          showViewOptions={Boolean(sharesCount)}
        />
      }
      hideFooter
    >
      <>
        {/*
        Prevent PersonalWorkspaceBanners from showing along with the
        empty Workspace banners.
        */}
        {sharesCount ? (
          <PersonalWorkspaceBanners
            workspaceId={workspace.identifier}
            workspaceType={workspace.type}
            isOwner={workspace.userIsOwner}
          />
        ) : null}

        {status && (
          <BillingStatusBanners
            workspaceId={workspaceId}
            sharesCount={sharesCount}
            billingStatus={status}
            cancelPeriodEnd={subscriptionEnd ?? undefined}
            isUserAdmin={isUserAdmin}
            isEducationWorkspace={isEducationWorkspace}
          />
        )}

        {subscription && (
          <WelcomeModalTrigger
            subscriptionDetails={subscription}
            workspace={workspace}
          />
        )}

        {/* Mounting the banner container to render them all */}
        {!isWorkspaceLibrariesRoute && (
          <BannerWrapper $hideMargins="top">
            <Banner />
          </BannerWrapper>
        )}

        {isSmallerThanTablet && (
          <SearchBarWrapper>
            <SearchBar
              search={debouncedSearch}
              onSearchChange={setSearchValue}
              userRole={workspace.userRole}
            />
          </SearchBarWrapper>
        )}

        <Switch>
          <Route
            exact
            path={[
              routes.WORKSPACE_PROJECT.template(),
              routes.WORKSPACE_DRAFTS.template(),
            ]}
            render={({ match: { params } }: ProjectsViewProps) => (
              <ProjectsView
                workspace={workspace}
                projectId={'projectId' in params ? params.projectId : undefined}
                search={search}
                debouncedSearch={debouncedSearch}
                userIsEditor={isUserEditor}
                userCanRenameProject={
                  isDraftsRoute(path)
                    ? false
                    : isUserEditor || canUserAdministerShares
                }
              />
            )}
          />
          {canUserAdministerShares && (
            <Route
              exact
              path={routes.WORKSPACE_TRASH.template()}
              render={({ match: { params } }: WorkspaceTrashRouteParams) => (
                <TrashList
                  workspaceId={params.workspaceId!}
                  search={debouncedSearch}
                />
              )}
            />
          )}
          <Route
            exact
            path={routes.WORKSPACE_TRASH_PROJECT.template()}
            render={({ match: { params } }: ProjectsViewProps) => (
              <ProjectsView
                workspace={workspace}
                projectId={'projectId' in params ? params.projectId : undefined}
                search={search}
                debouncedSearch={debouncedSearch}
                userIsEditor={isUserEditor}
                userCanRenameProject={false}
              />
            )}
          />
          <Route
            exact
            path={routes.WORKSPACE_SHARES.template()}
            render={({ match }: WorkspaceSharesRouteParams) => (
              <WorkspaceSharesList
                workspaceId={match.params.workspaceId!}
                workspace={workspace}
                search={debouncedSearch}
                filter={showDocumentsInProjects ? null : 'NO_PROJECT'}
              />
            )}
          />
          <Route
            exact
            path={routes.WORKSPACE_SHARED_WITH_ME.template()}
            render={({ match }: WorkspaceSharedWithMeRouteParams) => (
              <WorkspaceSharesList
                workspaceId={match.params.workspaceId!}
                search={debouncedSearch}
                filter="SHARED_WITH_ME"
              />
            )}
          />
          <Route
            exact
            path={routes.WORKSPACE_TEMPLATES.template()}
            render={({ match }: WorkspaceSharedWithMeRouteParams) => (
              <WorkspaceSharesList
                workspaceId={match.params.workspaceId!}
                search={debouncedSearch}
                filter="TEMPLATE"
              />
            )}
          />
          <Route
            exact
            path={routes.WORKSPACE_LIBRARIES.template()}
            render={({ match }: WorkspaceLibrariesRouteParams) => (
              <WorkspaceSharesList
                workspaceId={match.params.workspaceId!}
                filter="LIBRARY"
                search={debouncedSearch}
                isMember={isMember}
              />
            )}
          />
        </Switch>
      </>
    </WorkspaceSidebarLayout>
  )
}

export default WorkspaceDocumentsView
